import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATIC_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import ProductCard from "@/components/ProductCard";
import { TID } from "@/constants/testIds";
import { ArrowRight, Flame, Scissors, Gem, MoveRight, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

const HERO_SLIDES = [
    {
        id: "spice-1",
        img: `${STATIC_BASE}/products/royal-garam-masala-g0-bdae4142.png`,
        vertical: "spices",
        eyebrow: "— 01 · The Spice House",
        title: "Roasted for the hour.",
        titleItalic: "Milled the same afternoon.",
        subtitle: "Twelve whole spices, hand-toasted over an open flame in our Noida atelier and stone-ground within four hours. Our Royal Garam Masala reaches you within twenty-eight days of its first roast.",
        cta: "Enter the Spice House",
        to: "/shop/masalas",
        cap: "Signature blend",
        capTitle: "Royal Garam Masala — small batch",
        capSub: "Twelve spices · one afternoon · Noida atelier",
    },
    {
        id: "decor-1",
        img: `${STATIC_BASE}/products/banarasi-table-runner-g0-a0ca0371.png`,
        vertical: "home-decor",
        eyebrow: "— 02 · The Fabric House",
        title: "Zari for the table.",
        titleItalic: "Emerald for the evening.",
        subtitle: "Hand-woven Banarasi silk-velvet runners, gold zari borders drawn by hand from a single spool. Set the table with a piece that outlives the dinner party.",
        cta: "Enter the Fabric House",
        to: "/shop/home-decor",
        cap: "House textiles",
        capTitle: "Banarasi Emerald Table Runner",
        capSub: "Silk-velvet · gold zari border · Varanasi",
    },
    {
        id: "jewel-1",
        img: `${STATIC_BASE}/products/kundan-choker-set-g0-8908447f.png`,
        vertical: "jewelry",
        eyebrow: "— 03 · The Jewel House",
        title: "Bridal weight,",
        titleItalic: "everyday grace.",
        subtitle: "Uncut kundan cabochons set stone-by-stone in 22k-plated brass. Nickel-free, hypoallergenic. A shrouded bridal set that reads equally at a summer garden lunch.",
        cta: "Enter the Jewel House",
        to: "/shop/jewelry",
        cap: "Bridal collection",
        capTitle: "Kundan Royal Choker Set",
        capSub: "Uncut stones · pearl drop · Jaipur",
    },
    {
        id: "spice-2",
        img: `${STATIC_BASE}/products/kashmiri-red-chilli-g0-9f6bd9b6.png`,
        vertical: "spices",
        eyebrow: "— 01 · The Spice House",
        title: "Kashmiri crimson.",
        titleItalic: "Gentle heat.",
        subtitle: "Sun-dried in the Kupwara valley for ninety days, milled twice to a velvet finish. Prized by chefs for its ruby colour without overwhelming the palate.",
        cta: "See all single spices",
        to: "/shop/masalas",
        cap: "Single origin",
        capTitle: "Kashmiri Red Chilli Powder",
        capSub: "Ninety-day sun-drying · Kupwara valley",
    },
    {
        id: "decor-2",
        img: `${STATIC_BASE}/products/brass-cushion-cover-set-g0-0a3d84c7.png`,
        vertical: "home-decor",
        eyebrow: "— 02 · The Fabric House",
        title: "Geometry in gold.",
        titleItalic: "Silk-velvet in ink.",
        subtitle: "Hexagonal kalabattu-wire embroidery, laid down stitch by stitch on midnight silk-velvet. Sold as a set of two — because a sofa should be dressed in pairs.",
        cta: "Shop the drawing room",
        to: "/shop/home-decor",
        cap: "Drawing-room set",
        capTitle: "Kalabattu Geometric Cushion Set",
        capSub: "Silk-velvet · gold wire · pair of two",
    },
    {
        id: "jewel-2",
        img: `${STATIC_BASE}/products/polki-jhumka-earrings-g0-1adc5709.png`,
        vertical: "jewelry",
        eyebrow: "— 03 · The Jewel House",
        title: "Chandeliers",
        titleItalic: "for the ear.",
        subtitle: "Polki-cut mirror-glass petals cascading into fresh-water pearl fringes. Lightweight brass core, so you can wear them all the way through the last dance.",
        cta: "Shop all earrings",
        to: "/shop/jewelry",
        cap: "Statement earrings",
        capTitle: "Polki Chandelier Jhumka",
        capSub: "Mirror-glass petals · pearl fringe · Jaipur",
    },
];

// For the 3 vertical cards below hero (kept from previous design)
const MASALA_IMG = `${STATIC_BASE}/products/royal-garam-masala-84faff1a.png`;
const DECOR_IMG = `${STATIC_BASE}/uploads/prod-24.jpeg`;
const JEWELRY_IMG = `${STATIC_BASE}/products/kundan-choker-set-515e822e.png`;

// Small hook — reveal children when scrolled into view
function useReveal(threshold = 0.15) {
    const ref = useRef(null);
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || seen) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && setSeen(true)),
            { threshold, rootMargin: "0px 0px -60px 0px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [threshold, seen]);
    return [ref, seen];
}

export default function Home() {
    const { t, tr } = useI18n();
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    }, []);

    // Featured: pick 2 from each of the 3 verticals
    const featured = useMemo(() => {
        const buckets = { masalas: [], "home-decor": [], jewelry: [] };
        products.forEach((p) => { if (buckets[p.vertical]) buckets[p.vertical].push(p); });
        return [
            ...buckets.masalas.slice(0, 2),
            ...buckets["home-decor"].slice(0, 3),
            ...buckets.jewelry.slice(0, 2),
        ];
    }, [products]);

    // Vertical strips: 4 products from each vertical for a horizontal marquee
    const stripMasalas = products.filter((p) => p.vertical === "masalas").slice(0, 6);
    const stripDecor = products.filter((p) => p.vertical === "home-decor").slice(0, 6);
    const stripJewelry = products.filter((p) => p.vertical === "jewelry").slice(0, 6);

    return (
        <div>
            <HeroSection t={t} />
            <MarqueeStrip />
            <VerticalsSection t={t} />
            <VerticalStripSection
                testId="strip-masalas"
                eyebrow="— 03 · The Kitchen"
                title={t("vert.masalas.title")}
                description={t("vert.masalas.desc")}
                icon={<Flame className="w-4 h-4" strokeWidth={1.5} />}
                to="/shop/masalas"
                products={stripMasalas}
            />
            <VerticalStripSection
                testId="strip-decor"
                eyebrow="— 04 · The Drawing Room"
                title={t("vert.decor.title")}
                description={t("vert.decor.desc")}
                icon={<Scissors className="w-4 h-4" strokeWidth={1.5} />}
                to="/shop/home-decor"
                products={stripDecor}
                reverse
            />
            <VerticalStripSection
                testId="strip-jewelry"
                eyebrow="— 05 · The Vanity"
                title={t("vert.jewelry.title")}
                description={t("vert.jewelry.desc")}
                icon={<Gem className="w-4 h-4" strokeWidth={1.5} />}
                to="/shop/jewelry"
                products={stripJewelry}
            />
            <FeaturedSection t={t} featured={featured} />
            <FestivePromoSection />
            <CraftProcessSection t={t} />
            <PressBarSection t={t} />
            <NewsletterSection t={t} />
        </div>
    );
}

/* ---------------- HERO SLIDER ---------------- */
function HeroSection({ t }) {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const total = HERO_SLIDES.length;

    // Auto-advance every 6.5s
    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => setIdx((i) => (i + 1) % total), 6500);
        return () => clearInterval(id);
    }, [paused, total]);

    const goto = (n) => setIdx(((n % total) + total) % total);
    const next = () => goto(idx + 1);
    const prev = () => goto(idx - 1);

    return (
        <section
            data-testid="hero-slider"
            className="relative h-[560px] sm:h-[640px] lg:h-[720px] w-full overflow-hidden bg-ink-900"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Slides — crossfade */}
            {HERO_SLIDES.map((s, i) => (
                <div
                    key={s.id}
                    className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${i === idx ? "opacity-100 z-10" : "opacity-0 z-0"}`}
                    aria-hidden={i !== idx}
                >
                    <img
                        src={s.img}
                        alt={s.capTitle}
                        className={`w-full h-full object-cover ${i === idx ? "animate-ken-burns" : ""}`}
                    />
                    {/* dark editorial gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-ink-900/90 via-ink-900/50 to-ink-900/20" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-transparent to-ink-900/30" />
                </div>
            ))}

            {/* Ambient brass glow */}
            <div
                className="pointer-events-none absolute -top-1/3 -right-1/4 w-[900px] h-[900px] rounded-full opacity-[0.16] blur-[120px] z-10"
                style={{ background: "radial-gradient(circle, #C5A059 0%, transparent 60%)" }}
            />

            {/* Slide content */}
            <div className="relative z-20 h-full max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 flex flex-col justify-center">
                <div className="max-w-3xl">
                    {HERO_SLIDES.map((s, i) => (
                        <div
                            key={s.id}
                            className={`transition-all duration-1000 ${i === idx ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 absolute pointer-events-none"}`}
                        >
                            <p className="text-[10px] sm:text-[11px] tracking-[0.32em] sm:tracking-[0.4em] uppercase text-brass-400 mb-5 sm:mb-7">
                                {s.eyebrow}
                            </p>
                            <h1 className="font-serif text-[44px] sm:text-[64px] lg:text-[84px] leading-[0.94] tracking-tight text-bone-100 mb-6 sm:mb-8">
                                {s.title}
                                <br />
                                <span className="italic text-brass-400">{s.titleItalic}</span>
                            </h1>
                            <p className="text-base sm:text-lg text-bone-100/85 max-w-xl font-light leading-relaxed mb-9 sm:mb-11">
                                {s.subtitle}
                            </p>
                            <div className="flex flex-wrap gap-4 sm:gap-6">
                                <Link
                                    to={s.to}
                                    data-testid={`hero-cta-${s.id}`}
                                    className="group inline-flex items-center gap-3 sm:gap-4 border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 px-6 sm:px-8 py-3.5 sm:py-4 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase"
                                >
                                    {s.cta}
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                                <Link
                                    to="/journal"
                                    className="text-bone-100/80 hover:text-brass-400 transition-colors px-2 sm:px-4 py-3.5 sm:py-4 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase link-hairline"
                                >
                                    The story of Keneth
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Caption plate — bottom right */}
            <div className="hidden md:block absolute right-6 lg:right-10 bottom-24 lg:bottom-28 z-20 max-w-[300px]">
                {HERO_SLIDES.map((s, i) => (
                    <div
                        key={s.id}
                        className={`bg-ink-800/85 backdrop-blur border border-brass-400/30 px-5 py-4 transition-all duration-700 ${i === idx ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 absolute inset-0 pointer-events-none"}`}
                    >
                        <p className="text-[9px] tracking-[0.28em] uppercase text-brass-400 mb-1">{s.cap}</p>
                        <p className="font-serif text-base text-bone-100 leading-tight">{s.capTitle}</p>
                        <p className="text-[10px] text-bone-300/80 mt-1.5 tracking-wide">{s.capSub}</p>
                    </div>
                ))}
            </div>

            {/* Dots + counter — bottom left */}
            <div className="absolute bottom-8 left-5 sm:left-6 lg:left-10 z-20 flex items-center gap-4 sm:gap-6">
                <span className="font-serif italic text-lg sm:text-xl text-brass-400 tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                    <span className="text-bone-300/50 mx-2">/</span>
                    <span className="text-bone-300/70 text-sm">{String(total).padStart(2, "0")}</span>
                </span>
                <div className="flex gap-2 sm:gap-2.5" role="tablist" aria-label="Hero slides">
                    {HERO_SLIDES.map((_, i) => (
                        <button
                            key={i}
                            data-testid={`hero-dot-${i}`}
                            role="tab"
                            aria-selected={i === idx}
                            aria-label={`Slide ${i + 1}`}
                            onClick={() => goto(i)}
                            className={`h-[3px] transition-all duration-500 ${i === idx ? "w-10 bg-brass-400" : "w-5 bg-bone-300/30 hover:bg-bone-300/60"}`}
                        />
                    ))}
                </div>
            </div>

            {/* Arrow controls — bottom right (mobile) / right edge (desktop) */}
            <div className="absolute bottom-8 right-5 sm:right-6 lg:right-10 z-20 flex gap-2">
                <button
                    data-testid="hero-prev"
                    onClick={prev}
                    aria-label="Previous slide"
                    className="w-11 h-11 sm:w-12 sm:h-12 border border-brass-400/40 text-brass-400 hover:bg-brass-400 hover:text-ink-900 hover:border-brass-400 transition-all flex items-center justify-center backdrop-blur bg-ink-900/30"
                >
                    <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <button
                    data-testid="hero-next"
                    onClick={next}
                    aria-label="Next slide"
                    className="w-11 h-11 sm:w-12 sm:h-12 border border-brass-400/40 text-brass-400 hover:bg-brass-400 hover:text-ink-900 hover:border-brass-400 transition-all flex items-center justify-center backdrop-blur bg-ink-900/30"
                >
                    <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                </button>
            </div>

            {/* Vertical label on right edge */}
            <div className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-4 origin-right">
                <div className="w-px h-16 bg-brass-400/40" />
                <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] tracking-[0.4em] uppercase text-brass-400/80">
                    Est. Noida · 2023
                </span>
                <div className="w-px h-16 bg-brass-400/40" />
            </div>

            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5 z-20">
                <div
                    key={idx + "-" + paused}
                    className="h-full bg-brass-400"
                    style={{
                        animation: paused ? "none" : `hero-progress 6500ms linear forwards`,
                    }}
                />
            </div>

            {/* keyframes injected inline for progress bar */}
            <style>{`@keyframes hero-progress { from { width: 0%; } to { width: 100%; } }`}</style>
        </section>
    );
}

function TrustStat({ value, label }) {
    return (
        <div>
            <div className="font-serif italic text-2xl sm:text-3xl text-brass-400">{value}</div>
            <div className="text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-bone-300/70 mt-2 leading-tight">{label}</div>
        </div>
    );
}

/* ---------------- MARQUEE ---------------- */
function MarqueeStrip() {
    const tags = [
        "Hand-block printed", "Stone-ground spices", "22k gold-plated brass", "Vegetable-tanned leather",
        "Small-batch roasting", "Zardosi embroidery", "Kundan set by hand", "Sanganer block prints",
        "Meenakari enamel", "Nawabi biryani masala", "Kanchipuram temple gold", "Complimentary EU delivery over €120",
    ];
    const doubled = [...tags, ...tags];
    return (
        <div className="border-y border-white/5 bg-ink-800/40 py-4 overflow-hidden">
            <div className="flex gap-10 sm:gap-16 animate-marquee whitespace-nowrap will-change-transform">
                {doubled.map((tag, i) => (
                    <span key={i} className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-bone-300/70 flex items-center gap-6">
                        {tag}
                        <span className="text-brass-400/60">◆</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

/* ---------------- 3 VERTICAL CARDS ---------------- */
function VerticalsSection({ t }) {
    const [ref, seen] = useReveal();
    return (
        <section ref={ref} className="py-20 sm:py-24 lg:py-32">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-12 sm:mb-16">
                    <div className="lg:col-span-4">
                        <p className={`text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                            — 02 · Provenance
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <h2 className={`font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 tracking-tight leading-none mb-5 sm:mb-6 transition-all duration-700 delay-100 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.verticals.title")}
                        </h2>
                        <p className={`text-base sm:text-lg text-bone-300 font-light max-w-2xl leading-relaxed transition-all duration-700 delay-200 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.verticals.sub")}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
                    <VerticalCard
                        seen={seen}
                        delay={200}
                        testId={TID.verticalMasalas}
                        to="/shop/masalas"
                        eyebrow="— The Spice House"
                        title={t("vert.masalas.title")}
                        desc={t("vert.masalas.desc")}
                        img={MASALA_IMG}
                        icon={<Flame className="w-4 h-4" strokeWidth={1.5} />}
                    />
                    <VerticalCard
                        seen={seen}
                        delay={350}
                        testId={TID.verticalDecor}
                        to="/shop/home-decor"
                        eyebrow="— The Fabric House"
                        title={t("vert.decor.title")}
                        desc={t("vert.decor.desc")}
                        img={DECOR_IMG}
                        icon={<Scissors className="w-4 h-4" strokeWidth={1.5} />}
                    />
                    <VerticalCard
                        seen={seen}
                        delay={500}
                        testId={TID.verticalJewelry}
                        to="/shop/jewelry"
                        eyebrow="— The Jewel House"
                        title={t("vert.jewelry.title")}
                        desc={t("vert.jewelry.desc")}
                        img={JEWELRY_IMG}
                        icon={<Gem className="w-4 h-4" strokeWidth={1.5} />}
                    />
                </div>
            </div>
        </section>
    );
}

function VerticalCard({ testId, to, eyebrow, title, desc, img, icon, seen, delay = 0 }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className={`group relative aspect-[4/5] card-image-wrap bg-ink-800 block transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <img src={img} alt="" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
            <div className="absolute top-4 sm:top-5 left-4 sm:left-5 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-brass-400/50 text-brass-400 bg-ink-900/50 backdrop-blur">
                {icon}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-10">
                <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-brass-400 mb-2 sm:mb-3">{eyebrow}</p>
                <h3 className="font-serif text-3xl sm:text-4xl text-bone-100 mb-2 sm:mb-3 leading-none">{title}</h3>
                <p className="text-xs sm:text-sm text-bone-100/80 font-light max-w-md mb-4 sm:mb-5 leading-relaxed">{desc}</p>
                <span className="inline-flex items-center gap-2 sm:gap-3 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-brass-400 group-hover:gap-4 sm:group-hover:gap-5 transition-all">
                    Discover <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </span>
            </div>
        </Link>
    );
}

/* ---------------- VERTICAL PRODUCT STRIP ---------------- */
function VerticalStripSection({ testId, eyebrow, title, description, icon, to, products, reverse = false }) {
    const [ref, seen] = useReveal();
    if (!products || products.length === 0) return null;
    return (
        <section ref={ref} data-testid={testId} className="py-16 sm:py-20 lg:py-28 border-t border-white/5 overflow-hidden">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-10 sm:mb-14 ${reverse ? "lg:direction-rtl" : ""}`}>
                    <div className="lg:col-span-5 flex flex-col justify-center">
                        <div className={`flex items-center gap-3 mb-4 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                            <span className="w-8 h-8 flex items-center justify-center border border-brass-400/40 text-brass-400">
                                {icon}
                            </span>
                            <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400">{eyebrow}</p>
                        </div>
                        <h2 className={`font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 tracking-tight leading-[1] mb-5 transition-all duration-700 delay-100 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {title}
                        </h2>
                        <p className={`text-sm sm:text-base text-bone-300 font-light leading-relaxed mb-6 sm:mb-8 max-w-lg transition-all duration-700 delay-200 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {description}
                        </p>
                        <Link
                            to={to}
                            className={`group inline-flex items-center gap-3 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-brass-400 self-start border-b border-brass-400/40 pb-1 hover:border-brass-400 hover:gap-5 transition-all duration-500 ${seen ? "opacity-100" : "opacity-0"}`}
                            style={{ transitionDelay: "300ms" }}
                        >
                            Explore the house <MoveRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="flex gap-4 sm:gap-6 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 scrollbar-hide snap-x snap-mandatory pb-3">
                            {products.map((p, i) => (
                                <div
                                    key={p.slug}
                                    className={`w-[220px] sm:w-[240px] shrink-0 snap-start transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                                    style={{ transitionDelay: `${300 + i * 90}ms` }}
                                >
                                    <ProductCard product={p} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------------- FEATURED GRID ---------------- */
function FeaturedSection({ t, featured }) {
    const [ref, seen] = useReveal();
    return (
        <section ref={ref} className="py-20 sm:py-24 lg:py-32 border-t border-white/5">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 sm:mb-16 gap-4 sm:gap-6">
                    <div>
                        <div className={`flex items-center gap-2 mb-3 sm:mb-4 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                            <Sparkles className="w-4 h-4 text-brass-400" strokeWidth={1.5} />
                            <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400">— 06 · Curated</p>
                        </div>
                        <h2 className={`font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 tracking-tight leading-none transition-all duration-700 delay-100 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.featured.title")}
                        </h2>
                        <p className={`text-base sm:text-lg text-bone-300 font-light mt-4 max-w-xl transition-all duration-700 delay-200 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.featured.sub")}
                        </p>
                    </div>
                    <Link to="/shop" className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-brass-400 link-hairline self-start md:self-auto">
                        View all →
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-10 sm:gap-y-14">
                    {featured.slice(0, 8).map((p, i) => (
                        <div
                            key={p.slug}
                            className={`transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <ProductCard product={p} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------- CRAFT PROCESS ---------------- */
function CraftProcessSection({ t }) {
    const [ref, seen] = useReveal();
    const steps = [
        { n: "01", label: "Sourcing", detail: "Whole spices from single-origin farms · silk-velvet from Karnataka · brass ingots from Moradabad." },
        { n: "02", label: "Making", detail: "Stone-milled the same afternoon · six months on the embroidery frame · stone-set by hand at the bench." },
        { n: "03", label: "Finishing", detail: "Hand-polished, patinated and packed in muslin — never plastic. Sealed within hours of the last touch." },
        { n: "04", label: "Dispatch", detail: "Complimentary EU delivery over €120 · gift-wrapped in kraft and silk · with a hand-written note from the maker." },
    ];
    return (
        <section ref={ref} className="py-20 sm:py-24 lg:py-32 border-t border-white/5">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12">
                    <div className="lg:col-span-5">
                        <p className={`text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4 sm:mb-6 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>— 07 · Craft</p>
                        <h2 className={`font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-bone-100 mb-6 transition-all duration-700 delay-100 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.craft.title")}
                        </h2>
                        <p className={`text-base sm:text-lg font-light leading-relaxed text-bone-100/85 mb-4 transition-all duration-700 delay-200 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.craft.p1")}
                        </p>
                        <p className={`text-base sm:text-lg font-light leading-relaxed text-bone-300 transition-all duration-700 delay-300 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            {t("sec.craft.p2")}
                        </p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <ol className="space-y-6 sm:space-y-8">
                            {steps.map((s, i) => (
                                <li
                                    key={s.n}
                                    className={`grid grid-cols-[auto_1fr] gap-5 sm:gap-8 items-start pb-6 sm:pb-8 border-b border-white/10 last:border-0 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                                    style={{ transitionDelay: `${200 + i * 120}ms` }}
                                >
                                    <div className="font-serif italic text-3xl sm:text-4xl text-brass-400 tabular-nums w-16">{s.n}</div>
                                    <div>
                                        <div className="text-[11px] tracking-[0.28em] uppercase text-bone-100 mb-2">{s.label}</div>
                                        <p className="text-sm sm:text-base text-bone-300 font-light leading-relaxed">{s.detail}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------------- PRESS BAR ---------------- */
function PressBarSection({ t }) {
    return (
        <section className="py-12 sm:py-16 border-t border-b border-white/5 bg-ink-800/40">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                <p className="text-[9px] sm:text-[10px] tracking-[0.4em] uppercase text-bone-300/60 text-center mb-6 sm:mb-8">{t("sec.press.title")}</p>
                <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 lg:gap-20 opacity-60">
                    <span className="font-serif italic text-lg sm:text-2xl text-bone-100/70">Kinfolk</span>
                    <span className="font-serif text-lg sm:text-2xl tracking-widest text-bone-100/70">MONOCLE</span>
                    <span className="font-serif italic text-lg sm:text-2xl text-bone-100/70">Vogue Living</span>
                    <span className="font-serif text-base sm:text-xl tracking-[0.3em] text-bone-100/70">CEREAL</span>
                    <span className="font-serif italic text-lg sm:text-2xl text-bone-100/70">Elle Décor</span>
                </div>
            </div>
        </section>
    );
}

/* ---------------- NEWSLETTER ---------------- */
function NewsletterSection({ t }) {
    const [ref, seen] = useReveal();
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    return (
        <section ref={ref} className="py-20 sm:py-24 lg:py-32">
            <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
                <p className={`text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>— The Dispatch</p>
                <h2 className={`font-serif text-4xl sm:text-5xl text-bone-100 tracking-tight leading-none mb-4 transition-all duration-700 delay-100 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                    {t("sec.newsletter.title")}
                </h2>
                <p className={`text-sm sm:text-base text-bone-300 font-light leading-relaxed mb-8 sm:mb-10 transition-all duration-700 delay-200 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                    {t("sec.newsletter.sub")}
                </p>
                <form
                    onSubmit={(e) => { e.preventDefault(); setSent(true); setTimeout(() => setSent(false), 4000); }}
                    className={`flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-xl mx-auto transition-all duration-700 delay-300 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                >
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("sec.newsletter.placeholder")}
                        data-testid="newsletter-email"
                        className="flex-1 bg-transparent border-b border-white/20 rounded-none text-bone-100 px-2 py-3 focus:outline-none focus:border-brass-400 transition-colors placeholder:text-bone-300/50"
                    />
                    <button
                        type="submit"
                        data-testid="newsletter-submit"
                        className="border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 px-6 sm:px-8 py-3 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase"
                    >
                        {t("sec.newsletter.submit")}
                    </button>
                </form>
                {sent && (
                    <p className="mt-6 text-[11px] tracking-[0.24em] uppercase text-brass-400 animate-fade-up">Received. Look for us in your inbox.</p>
                )}
            </div>
        </section>
    );
}

/* ---------------- FESTIVE PROMO — Diwali + Christmas ---------------- */
function FestivePromoSection() {
    const [ref, seen] = useReveal();
    const cards = [
        {
            to: "/festive/diwali",
            testId: "festive-promo-diwali",
            eyebrow: "— Diwali edit",
            title: "For the golden evenings.",
            note: "Zardosi, kundan, and warm lamp-light.",
            img: `${STATIC_BASE}/uploads/prod-27.jpeg`,
            date: "October 21 · 2026",
        },
        {
            to: "/festive/christmas",
            testId: "festive-promo-christmas",
            eyebrow: "— Christmas edit",
            title: "Beneath the tree. On the mantle.",
            note: "Zari baubles, velvet stockings, tree skirts.",
            img: `${STATIC_BASE}/uploads/prod-15.jpeg`,
            date: "December 25 · 2026",
        },
    ];
    return (
        <section ref={ref} className="py-16 sm:py-20 lg:py-24 border-t border-white/5">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                <div className="flex items-end justify-between mb-10 sm:mb-14">
                    <div>
                        <p className={`text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-3 transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                            — Two occasions · One atelier
                        </p>
                        <h2 className={`font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 tracking-tight leading-none max-w-3xl transition-all duration-700 delay-100 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                            Handmade for the days that matter.
                        </h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
                    {cards.map((c, i) => (
                        <Link
                            key={c.to}
                            to={c.to}
                            data-testid={c.testId}
                            className={`group relative block aspect-[5/6] lg:aspect-[7/5] card-image-wrap bg-ink-800 overflow-hidden transition-all duration-700 ${seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                            style={{ transitionDelay: `${200 + i * 150}ms` }}
                        >
                            <img src={c.img} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.03] transition-all duration-[1200ms]" />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-ink-900/20" />
                            <div
                                className="pointer-events-none absolute -bottom-1/3 -left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.25] blur-[100px]"
                                style={{ background: "radial-gradient(circle, #C5A059 0%, transparent 60%)" }}
                            />
                            <div className="absolute top-4 sm:top-5 right-4 sm:right-5 bg-ink-900/70 backdrop-blur border border-brass-400/30 px-3 py-1.5 text-[9px] tracking-[0.28em] uppercase text-brass-400">
                                {c.date}
                            </div>
                            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 lg:p-10">
                                <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-brass-400 mb-3">
                                    {c.eyebrow}
                                </p>
                                <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bone-100 leading-none mb-3 sm:mb-4 tracking-tight">
                                    {c.title}
                                </h3>
                                <p className="text-xs sm:text-sm text-bone-100/80 font-light max-w-md mb-4 sm:mb-5">{c.note}</p>
                                <span className="inline-flex items-center gap-3 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-brass-400 group-hover:gap-5 transition-all">
                                    Enter the edit <ArrowRight className="w-4 h-4" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

